(() => {
  if (window.spQuickbuy) return;

  /* ── Shared ─────────────────────────────────────────────────────────── */

  // One motion spec for everything this component animates: the house
  // easeOutQuint at 280ms, the same curve sp-quickbuy.css uses.
  const MOTION = { duration: 280, easing: 'cubic-bezier(0.22, 1, 0.36, 1)' };
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const desktop = window.matchMedia('(min-width: 768px)');

  const stats = { prefetched: 0, hits: 0, misses: 0, sheets: 0 };
  window.spQuickbuy = { stats, MOTION };

  const panelOf = (el) => (el && el.closest ? el.closest('.quickbuy-container') : null);

  function fadeIn(el) {
    if (!el || !el.animate || reducedMotion.matches) return;
    el.animate([{ opacity: 0 }, { opacity: 1 }], MOTION);
  }

  const HOVER_INTENT_MS = 90;
  const TTL_MS = 60 * 1000;
  const cache = new Map(); // absolute url -> { at, text: Promise<string> }
  const nativeFetch = window.fetch.bind(window);
  const idle = window.requestIdleCallback || ((fn) => setTimeout(fn, 1));
  const saveData = !!(navigator.connection && navigator.connection.saveData);

  const fresh = (url) => {
    const hit = cache.get(url);
    if (hit && Date.now() - hit.at < TTL_MS) return hit;
    if (hit) cache.delete(url);
    return null;
  };

  function adoptStylesheets(html) {
    idle(() => {
      const doc = new DOMParser().parseFromString(html, 'text/html');
      doc.querySelectorAll(':is(.section-main-product, .section-size-guide) link[rel="stylesheet"]').forEach((el) => {
        const href = el.getAttribute('href');
        if (href && !document.querySelector(`link[rel="stylesheet"][href="${CSS.escape(href)}"]`)) {
          document.head.insertAdjacentHTML('beforeend', el.outerHTML);
          stats.sheets += 1;
        }
      });
    });
  }

  function prefetch(url) {
    if (saveData || !url || !desktop.matches || fresh(url)) return;
    const entry = {
      at: Date.now(),
      text: nativeFetch(url, { method: 'GET', credentials: 'same-origin' }).then((res) => {
        if (!res.ok) throw new Error(`prefetch ${res.status}`);
        return res.text();
      }),
    };
    entry.text.catch(() => cache.delete(url));
    entry.text.then(adoptStylesheets, () => {});
    cache.set(url, entry);
    stats.prefetched += 1;
  }

  window.fetch = (input, init) => {
    const url = typeof input === 'string' ? new URL(input, location.href).href : null;
    const method = ((init && init.method) || 'GET').toUpperCase();
    const hit = url && method === 'GET' ? fresh(url) : null;
    if (!hit) {
      if (url && url.includes('/products/')) stats.misses += 1;
      return nativeFetch(input, init);
    }
    stats.hits += 1;
    const signal = init && init.signal;
    if (signal && signal.aborted) {
      return Promise.reject(signal.reason || new DOMException('Aborted', 'AbortError'));
    }
    cache.delete(url);
    return hit.text.then(
      (text) => new Response(text, { status: 200, headers: { 'Content-Type': 'text/html; charset=utf-8' } }),
      () => nativeFetch(input, init),
    );
  };

  const toggleUrl = (el) => {
    const cont = el && el.closest ? el.closest('.product-block .image-cont') : null;
    const toggle = cont && cont.querySelector('.quickbuy-toggle');
    return toggle && toggle.href ? { cont, url: toggle.href } : null;
  };

  let timer = 0;
  let pending = null;
  document.addEventListener('pointerover', (evt) => {
    if (evt.pointerType && evt.pointerType !== 'mouse') return;
    const target = toggleUrl(evt.target);
    if (!target || target.url === pending) return;
    clearTimeout(timer);
    pending = target.url;
    timer = setTimeout(() => { prefetch(target.url); pending = null; }, HOVER_INTENT_MS);
  }, { passive: true });

  document.addEventListener('pointerout', (evt) => {
    const target = toggleUrl(evt.target);
    if (!target || (evt.relatedTarget && target.cont.contains(evt.relatedTarget))) return;
    clearTimeout(timer);
    pending = null;
  }, { passive: true });

  function showActiveMedia(qbc) {
    qbc.querySelectorAll('.main-image .slider').forEach((slider) => {
      const active = slider.querySelector('.slider__item.is-active');
      if (active && active.offsetLeft) slider.scrollTo({ left: active.offsetLeft, behavior: 'instant' });
    });
  }

  function sizePanel(qbc, inner) {
    const h = inner.clientHeight;
    if (!h) return;
    const block = qbc.closest('.product-block');
    if (block) {
      // Grid: the card reserves the panel's room with its own padding.
      if (!block.classList.contains('expanded')) return;
      block.style.paddingBottom = `${h + 20}px`;
    }
    qbc.style.height = `${h}px`;
  }

  document.addEventListener('on:quickbuy:after-open', (evt) => {
    const qbc = panelOf(evt.target);
    const inner = qbc && qbc.querySelector('.inner');
    if (!inner || !inner.childElementCount) return;
    showActiveMedia(qbc);
    sizePanel(qbc, inner);
    fadeIn(inner.querySelector('.quickbuy-content') || inner);
  });

  /* ── 3. OPTIONS ──────────────────────────────────────────────────────
   * SWITCH ON THE CLICK. variant-picker.js fetches the variant's section
   * HTML before the gallery changes (and only preloads after a 250ms dwell),
   * so a click waited ~600ms on the round trip with the old colour showing
   * under the new chip. Each chip carries its variant's photo id
   * (variant-picker.liquid, data-sp-media-id), so the gallery is switched
   * here at once; when the fetch lands main.js sets the same media again,
   * which changes nothing. Multi-option chips carry no id and keep vendor
   * timing.
   *
   * FADE THE NEW PHOTO. setActiveMedia jumps the slideshow with
   * `scrollToElement(el, 'instant')` (main.js ~L2715), a hard cut. The
   * incoming photo fades up instead, but only when an option click caused
   * it (a `change` in the form within OPTION_WINDOW_MS) and the active
   * media really changed: the arrows already slide, and a fade on a slide
   * reads as a flicker. */
  const OPTION_WINDOW_MS = 600;
  let optionChangeAt = -Infinity;

  document.addEventListener('change', (evt) => {
    const qbc = panelOf(evt.target);
    if (!qbc || !evt.target.closest('.product-form')) return;
    optionChangeAt = performance.now();
    const mediaId = evt.target.dataset && evt.target.dataset.spMediaId;
    const gallery = mediaId && qbc.querySelector('media-gallery');
    if (gallery && typeof gallery.setActiveMedia === 'function') {
      try { gallery.setActiveMedia(mediaId, true); } catch (e) { /* vendor path still runs */ }
    }
  }, true);

  document.addEventListener('on:media-gallery:change', (evt) => {
    const gallery = evt.target;
    if (!panelOf(gallery)) return;
    const active = gallery.querySelector('.main-image .slider__item.is-active');
    const id = active && active.dataset.mediaId;
    const changed = id && id !== gallery.dataset.spQbMedia;
    if (id) gallery.dataset.spQbMedia = id;
    if (changed && performance.now() - optionChangeAt <= OPTION_WINDOW_MS) {
      fadeIn(active.querySelector('img'));
    }
  });

  /* ── 4. KEYBOARD ─────────────────────────────────────────────────────
   * Focusing the card's link reveals the button (sp-quickbuy.css
   * :focus-within), so a Tab onto it is the same intent signal as hover. */
  document.addEventListener('focusin', (evt) => {
    const target = toggleUrl(evt.target);
    if (target) prefetch(target.url);
  });
})();
